import { useCallback, useContext, useMemo } from 'react';
import { useHubTagTemplatesContext } from '../../../contexts/HubTagTemplatesContext/HubTagTemplatesContext';
import { useMemoryBankContext } from '../../../contexts/MemoryBankContext/MemoryBankContext';
import { useMemoryIconsContext } from '../../../contexts/MemoryIconsContext/MemoryIconsContext';
import { useCharacterExpressionsContext } from '../../../contexts/CharacterExpressionsContext/CharacterExpressionsContext';
import { AudiosContext } from '../../pages/AudioLibraryPage/AudioLibraryContainer/AudiosProvider/AudiosProvider';
import { api } from '../../api';
import type { Character } from '../../../dorian-shared/types/character/Character';
import type { Branch } from '../../../dorian-shared/types/branch/Branch';
import type { AnswerTypeItem, ModifierTypeItem } from './transformNodeToBranchDetails';
import type { ConverterContext } from './selectRenderState';
import { deriveAudioBucketUrl } from './deriveAudioBucketUrl';
import type { TagTemplate, HubResolveMaps } from './hubResolve';
import type { Audio } from '../../../dorian-shared/types/audio/audio';

const EMPTY_AUDIOS: Audio[] = [];

export interface EmulatorFrameDataOptions {
  episodeUuid: string;
  branches: Branch[] | undefined;
  storyCharacters: Character[];
  answerTypes: AnswerTypeItem[] | undefined;
  modifierTypes: ModifierTypeItem[] | undefined;
}

export interface EmulatorFrameData {
  converterCtx: ConverterContext;
  fetchEtalon: (branchId: number) => Promise<unknown>;
}

/**
 * Assembles all data needed to produce one emulator frame: converter context
 * (characters, expressions, hub resolve maps, audios, answer/modifier type lists)
 * and a fetcher for the HTTP etalon used by the dev-mode diff tool.
 *
 * Reads its own contexts so Preview.tsx stays free of assembly logic.
 */
export function useEmulatorFrameData(options: EmulatorFrameDataOptions): EmulatorFrameData {
  const {
    episodeUuid,
    branches,
    storyCharacters,
    answerTypes,
    modifierTypes,
  } = options;

  const expressions = useCharacterExpressionsContext();
  const { templates: tagTemplates } = useHubTagTemplatesContext();
  const { memories } = useMemoryBankContext();
  const { memoryIcons } = useMemoryIconsContext();

  // Destructure audios from context so the stable array reference (not a fresh ?? []
  // expression) enters the useMemo dep — AudiosProvider memoizes its value object,
  // so audios identity only changes when the audios list actually updates.
  const audiosContext = useContext(AudiosContext);
  const { audios } = audiosContext ?? { audios: EMPTY_AUDIOS };

  const hubResolveMaps = useMemo((): HubResolveMaps => {
    const branchMap = (branches ?? []).reduce<Record<number, string | null>>((acc, b) => {
      acc[b.id] = b.title ?? null;
      return acc;
    }, {});

    const variableMap = memories.reduce<Record<number, string>>((acc, m) => {
      acc[m.id] = m.name;
      return acc;
    }, {});

    // iconMap first so tagTemplateMap can resolve icon URLs in O(1),
    // mirroring _buildHubTagTemplateMap on the server.
    const iconMap = memoryIcons.reduce<Record<number, string | null>>((acc, icon) => {
      acc[icon.id] = icon.url;
      return acc;
    }, {});

    const tagTemplateMap = (tagTemplates ?? []).reduce<Record<number, TagTemplate>>(
      (acc, t) => {
        if (t.id != null) {
          const icon: string | null = t.iconId ? (iconMap[t.iconId] ?? null) : null;
          // HubTagTemplate lacks index signature; spread captures all runtime fields into TagTemplate.
          acc[t.id] = { ...(t as unknown as TagTemplate), icon };
        }
        return acc;
      },
      {},
    );

    return { branchMap, variableMap, tagTemplateMap, iconMap };
  }, [branches, memories, tagTemplates, memoryIcons]);

  const audioPath = useMemo(() => deriveAudioBucketUrl(audios ?? []), [audios]);

  const converterCtx = useMemo((): ConverterContext => ({
    characters: storyCharacters,
    expressions,
    maps: hubResolveMaps,
    audios,
    audioPath,
    answerTypes: answerTypes ?? [],
    modifierTypes: modifierTypes ?? [],
  }), [storyCharacters, expressions, hubResolveMaps, audios, audioPath, answerTypes, modifierTypes]);

  // Uses the same axios instance (baseURL + auth headers) as all other API calls —
  // a bare fetch() would hit the SPA host and return HTML instead of JSON.
  const fetchEtalon = useCallback(
    (branchId: number) => api
      .get<{ branch: unknown }>(`/v1/preview/${episodeUuid}/branches/${branchId}`)
      .then((r) => r.data.branch),
    [episodeUuid],
  );

  return { converterCtx, fetchEtalon };
}
